import * as React from 'react';

import { ModalName } from '@components/modals/types.ts';

interface ModalContextType {
	activeModal: ModalName | null;
	openModal: (modalName: ModalName, data?: any) => void;
	closeModal: () => void;
	isModalOpen: (modalName: ModalName) => boolean;
	getModalData: <T>(modalName: ModalName) => T | null;
}

const ModalContext = React.createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [activeModal, setActiveModal] = React.useState<ModalName | null>(null);
	const [modalData, setModalData] = React.useState<Record<string, any>>({});

	const openModal = (name: ModalName, data?: any) => {
		setActiveModal(name);
		if (data) {
			setModalData(prev => ({ ...prev, [name]: data }));
		}
	};

	const closeModal = () => {
		setActiveModal(null);
	};

	const getModalData = <T,>(name: ModalName): T | null => {
		return (modalData[name] as T) || null;
	};

	const isModalOpen = (modalName: ModalName) => {
		return activeModal === modalName;
	};

	return (
		<ModalContext.Provider value={{ activeModal, openModal, closeModal, isModalOpen, getModalData }}>
			{children}
		</ModalContext.Provider>
	);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useModals = () => {
	const context = React.useContext(ModalContext);
	if (context === undefined) {
		throw new Error('useModals must be used within a ModalProvider');
	}
	return context;
};
